/**
 * AdminKpis — full operator dashboard. R2 rewrite.
 * Preserves existing testids: admin-kpis, kpi-refresh, kpi-ggr-<w>, kpi-dep-<w>,
 * kpi-wd-paid-<w>, kpi-wd-pending-<w>, kpi-active, kpi-signups, kpi-kyc,
 * kpi-chat, kpi-rtp, ggr-chart.
 */
import { useEffect, useState } from "react";
import { RefreshCw, TrendingUp, TrendingDown, Users, ArrowDownToLine, ArrowUpFromLine, IdCard, MessageSquare } from "lucide-react";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import Button from "../components/ui-r1/Button";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";

const DATA_VIZ = ["#FFC947", "#FF3B7A", "#8B5CF6", "#22D3AA", "#EF4444"];

export default function AdminKpis() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/admin/kpis");
      setData(data);
    } catch (e) { setError(extractError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-[1440px] mx-auto space-y-6" data-testid="admin-kpis">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-2">KPIs</div>
          <h1 className="font-display italic text-3xl sm:text-4xl text-ink">Live dashboard</h1>
        </div>
        <Button onClick={load} variant="ghost" size="sm" data-testid="kpi-refresh">
          <RefreshCw className={"w-3 h-3 " + (loading ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>

      {error && (
        <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2">{error}</div>
      )}

      {loading && !data ? (
        <Card><CardBody className="text-sm text-ink-2">Loading…</CardBody></Card>
      ) : !data ? (
        <Card><CardBody className="text-sm text-ink-2 italic">No data</CardBody></Card>
      ) : (
        <Content data={data} />
      )}
    </div>
  );
}

function Content({ data }) {
  return (
    <div className="space-y-6">
      {/* GGR row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.windows.map((w, i) => (
          <StatCard
            key={w}
            testid={`kpi-ggr-${w}`}
            label={`GGR · ${w}`}
            value={`$${data.ggr[w].toFixed(2)}`}
            tone={data.ggr[w] >= 0 ? "gold" : "danger"}
            icon={data.ggr[w] >= 0 ? TrendingUp : TrendingDown}
          />
        ))}
      </div>

      {/* Deposits + withdrawals side-by-side per window */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.windows.map((w) => (
          <Card key={w}>
            <CardBody>
              <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-3">{w}</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase text-ink-2">Deposits</div>
                  <div className="font-mono text-gold text-lg mt-1" data-testid={`kpi-dep-${w}`}>${data.deposits[w].toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-ink-2">Withdrawals paid</div>
                  <div className="font-mono text-ink text-lg mt-1" data-testid={`kpi-wd-paid-${w}`}>${data.withdrawals[w].paid_usd.toFixed(2)}</div>
                  {data.withdrawals[w].pending_count > 0 && (
                    <Badge variant="danger" className="mt-1" data-testid={`kpi-wd-pending-${w}`}>
                      {data.withdrawals[w].pending_count} pending
                    </Badge>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Player / queue snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniStat label="Active players 7d" value={data.active_players_7d} icon={Users} tone="pink" testid="kpi-active" />
        <MiniStat label="Signups 30d" value={data.signups["30d"]} icon={ArrowDownToLine} tone="cyan" testid="kpi-signups" />
        <MiniStat label="KYC queue" value={data.kyc_queue_depth} tone={data.kyc_queue_depth > 0 ? "danger" : "muted"} icon={IdCard} testid="kpi-kyc" />
        <MiniStat label="Open chats" value={data.chat_open_count} sub={`${data.chat_unread_count} unread`} tone={data.chat_unread_count > 0 ? "danger" : "muted"} icon={MessageSquare} testid="kpi-chat" />
      </div>

      {/* RTP + provider donut */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardBody>
            <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-3">Actual RTP · 30d, real mode</div>
            <div className="font-display italic gold-text text-4xl" data-testid="kpi-rtp">
              {data.rtp_30d.actual !== null ? `${(data.rtp_30d.actual * 100).toFixed(2)}%` : "—"}
            </div>
            <div className="text-[10px] text-ink-3 mt-2">
              Target {(data.rtp_30d.target * 100).toFixed(2)}% · wagered ${data.rtp_30d.wagered_usd.toFixed(2)} · won ${data.rtp_30d.won_usd.toFixed(2)}
            </div>
            {data.rtp_30d.actual !== null && (
              <div className="mt-4 h-1.5 rounded-full bg-highest overflow-hidden">
                <div
                  className="h-full bg-gold-gradient"
                  style={{ width: `${Math.min(100, (data.rtp_30d.actual / data.rtp_30d.target) * 100)}%` }}
                />
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-3">Deposits by provider · 30d</div>
            <DonutProvider data={data.deposits_by_provider_30d} />
          </CardBody>
        </Card>
      </div>

      {/* GGR trend bar chart */}
      <Card>
        <CardBody>
          <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-3">Daily GGR · last 30d</div>
          <BarChart data={data.ggr_trend_30d} />
        </CardBody>
      </Card>
    </div>
  );
}

function StatCard({ testid, label, value, tone = "gold", icon: Icon }) {
  const tones = {
    gold:   "from-gold/20 to-gold/5 border-gold/40 text-gold",
    danger: "from-danger/20 to-danger/5 border-danger/40 text-danger",
    pink:   "from-pink/20 to-pink/5 border-pink/40 text-pink",
  };
  return (
    <div
      className={`rounded-xl border p-5 bg-gradient-to-br ${tones[tone] || tones.gold}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-2">{label}</div>
        {Icon && <Icon className="w-4 h-4" />}
      </div>
      <div className="font-display italic text-4xl text-ink">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, sub, tone = "muted", icon: Icon, testid }) {
  const dot = {
    danger: "text-danger",
    pink: "text-pink",
    cyan: "text-[color:var(--success)]",
    muted: "text-ink-3",
  };
  return (
    <div className="rounded-xl border border-line bg-elevated p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-3.5 h-3.5 ${dot[tone] || dot.muted}`} />}
        <div className="text-[10px] tracking-widest uppercase text-ink-3">{label}</div>
      </div>
      <div className={"font-mono text-2xl " + (tone === "danger" ? "text-danger" : "text-ink")}>{value}</div>
      {sub && <div className={"text-[10px] mt-1 " + dot[tone]}>{sub}</div>}
    </div>
  );
}

function BarChart({ data }) {
  if (!data || data.length === 0) return <div className="text-xs text-ink-2 italic">No data.</div>;
  const max = Math.max(...data.map((d) => Math.abs(d.ggr)), 1);
  return (
    <div className="flex items-end gap-1 h-48" data-testid="ggr-chart">
      {data.map((d) => {
        const h = Math.max(2, (Math.abs(d.ggr) / max) * 100);
        const positive = d.ggr >= 0;
        return (
          <div key={d.day} className="flex-1 flex flex-col items-center justify-end min-w-0 group relative" title={`${d.day}: $${d.ggr.toFixed(2)}`}>
            <div
              className={
                "w-full min-h-[2px] rounded-t transition-all group-hover:brightness-110 " +
                (positive ? "bg-gold-gradient" : "bg-danger")
              }
              style={{ height: `${h}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

function DonutProvider({ data }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <div className="text-xs text-ink-2 italic">No confirmed deposits.</div>;
  const total = entries.reduce((s, [, v]) => s + v, 0);
  let angle = 0;
  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 100 100" className="w-32 h-32 shrink-0">
        {entries.map(([k, v], i) => {
          const pct = v / total;
          const a2 = angle + pct * 360;
          const large = pct > 0.5 ? 1 : 0;
          const [x1, y1] = polar(50, 50, 40, angle);
          const [x2, y2] = polar(50, 50, 40, a2);
          const d = `M50,50 L${x1},${y1} A40,40 0 ${large} 1 ${x2},${y2} Z`;
          angle = a2;
          return <path key={k} d={d} fill={DATA_VIZ[i % DATA_VIZ.length]} />;
        })}
        <circle cx="50" cy="50" r="22" fill="var(--bg-elevated)" />
        <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" fill="var(--text-primary)" fontSize="9" fontFamily="Playfair Display" fontStyle="italic">
          ${total.toFixed(0)}
        </text>
      </svg>
      <ul className="text-xs space-y-2 flex-1 min-w-0">
        {entries.map(([k, v], i) => (
          <li key={k} className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: DATA_VIZ[i % DATA_VIZ.length] }} />
            <span className="uppercase tracking-widest text-[10px] text-ink-2 flex-1 truncate">{k}</span>
            <span className="font-mono text-ink">${v.toFixed(2)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function polar(cx, cy, r, deg) {
  const rad = (deg - 90) * (Math.PI / 180);
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}
